import { useEffect, useRef, useState } from "react";

const stats = [
  {
    value: 137,
    suffix: "%",
    label: "Higher ADR",
    detail:
      "Branded properties command an average daily rate 137% above the unbranded market median in comparable neighborhoods.",
    source: "AirDNA + STR benchmark, 2024",
  },
  {
    value: 64,
    suffix: "%",
    label: "More Earnings as Superhost",
    detail:
      "Superhosts earn 64% more than regular hosts, and nights booked with Superhosts grew 12% year-over-year from Q3 2023 to Q3 2024 — nearly double the rate of the overall platform.",
    source: "iPropertyManagement (2025); The Offer Sheet (2025)",
  },
  {
    value: 3.4,
    suffix: "×",
    decimals: 1,
    label: "RevPAR Uplift",
    detail:
      "Revenue per available night triples when identity, interiors and channel strategy work as one system.",
    source: "24-property longitudinal, 12mo pre/post",
  },
  {
    value: 61,
    suffix: "%",
    label: "Direct Bookings",
    detail:
      "A brand of your own means guests find you, not Airbnb — reducing platform commissions by up to 61%.",
    source: "Client-reported channel mix, 2024",
  },
];

function useCountUp(target, decimals = 0) {
  const [val, setVal] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);

  useEffect(() => {
    if (!ref.current) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting && !started.current) {
            started.current = true;
            const start = performance.now();
            const duration = 1600;
            const from = 0;
            const step = (now) => {
              const t = Math.min(1, (now - start) / duration);
              const eased = 1 - Math.pow(1 - t, 3);
              setVal(from + (target - from) * eased);
              if (t < 1) requestAnimationFrame(step);
              else setVal(target);
            };
            requestAnimationFrame(step);
          }
        });
      },
      { threshold: 0.3 }
    );
    io.observe(ref.current);
    return () => io.disconnect();
  }, [target]);

  return [ref, decimals === 0 ? Math.round(val) : val.toFixed(decimals)];
}

function StatCard({ stat, index }) {
  const [ref, display] = useCountUp(stat.value, stat.decimals || 0);
  return (
    <div
      ref={ref}
      data-testid={`stat-card-${index}`}
      className="border-t border-black/15 pt-8 pb-2"
    >
      <p className="overline mb-6">{String(index + 1).padStart(2, "0")}</p>
      <div className="flex items-start">
        <span
          className="font-serif-editorial text-[5.5rem] md:text-[7rem] lg:text-[8rem] leading-[0.85] tracking-[-0.04em] text-[#1A1A1A] font-light"
          data-testid={`stat-value-${index}`}
        >
          {display}
        </span>
        <span className="font-serif-editorial text-4xl md:text-5xl lg:text-6xl text-[#A89680] ml-1 mt-4">
          {stat.suffix}
        </span>
      </div>
      <p className="mt-4 font-serif-editorial italic text-2xl text-[#6B5C4D]">
        {stat.label}
      </p>
      <p className="mt-4 text-[#5C564D] leading-relaxed max-w-md">
        {stat.detail}
      </p>
      <p className="mt-3 text-xs uppercase tracking-[0.2em] text-[#A89680]">
        Source · {stat.source}
      </p>
    </div>
  );
}

export default function Stats() {
  return (
    <section
      id="stats"
      data-testid="statistics-section"
      className="relative py-24 md:py-32 lg:py-40 bg-[#F2EEE6]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-12 gap-8 mb-20 md:mb-28">
          <div className="col-span-12 md:col-span-5 reveal">
            <p className="overline mb-6">The Value of a Brand</p>
            <h2 className="font-serif-editorial text-4xl sm:text-5xl lg:text-6xl leading-[0.98] tracking-[-0.02em] text-[#1A1A1A] font-light">
              The price
              <br />
              <span className="italic text-[#6B5C4D]">of a name,</span>
              <br />
              measured.
            </h2>
          </div>
          <div className="col-span-12 md:col-span-6 md:col-start-7 reveal">
            <p className="text-[#5C564D] text-lg leading-relaxed">
              Data across our portfolio and the broader luxury short-stay
              market shows a consistent pattern: guests pay a premium — and
              return more often — for properties that feel like a
              destination, not a listing. Every metric below is real, audited
              and pulled from operating properties.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-x-16 md:gap-y-20">
          {stats.map((s, i) => (
            <div key={i} className="reveal">
              <StatCard stat={s} index={i} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
